/**
 * 设计态辅助：拉一次预览拆解、机型分组、资产编码对照。
 * 「拉一次」失败就是真错误：**禁止**回退内置样例设备冒充成功（docs/05-连接数据.md §1.1/§3）。
 * 后续版本由浏览器直连公网（线 B）取代本机网关预览口。
 */
import type { SceneObject } from '@/types/scene'
import type { DeviceSnapshot } from '@/types/realtime'
import { resolvePointsContainerPath, type FieldMapping, type PathExpr, type SourceShape } from '@/types/sourceAdapter'
import { convertSourceToSnapshots } from './sourceAdapter'
import { getByPath } from './sourceAdapterPath'
import { explainRuntimeConnectionError } from './explainRuntimeConnectionError'

export interface SourcePointPreview {
  key: string
  name: string
  value: string
  time?: string
}

export interface SourceDevicePreview {
  assetCode: string
  deviceName?: string
  /**
   * 分组键：优先 identity.deviceTypeSourcePath 真值；缺省或空时退化为点位指纹 / `_default`。
   * **禁止**把本字段写进 fieldMappings.deviceTypeMatch.equals（指纹无法在运行时匹配）。
   * 登记映射请用 `deviceTypeValue`。
   */
  deviceTypeKey: string
  /**
   * identity.deviceTypeSourcePath 读到的非空真值；未配置路径或字段为空时为 undefined。
   * 与 deviceTypeKey 的点位指纹兜底刻意分离。
   */
  deviceTypeValue?: string
  status?: string
  netStatus?: string
  points: SourcePointPreview[]
  snapshot?: DeviceSnapshot
}

export interface AssetMatchReport {
  matched: Array<{ assetCode: string; objectId: string; objectName: string; deviceName?: string }>
  /** 场景有对象（含 assetCode）但源里没有 */
  sceneOnly: Array<{ assetCode: string; objectId: string; objectName: string }>
  /** 源有设备但场景无对应 assetCode */
  sourceOnly: Array<{ assetCode: string; deviceName?: string }>
}

export interface DesignConnectionDraft {
  templateId: string
  baseUrl: string
  organizationId: string
  username: string
  password: string
  pollIntervalMs: number
}

/**
 * 用 sessionStorage 而非 localStorage：账号密码只是"拉一次"用的临时草稿（见方案 §2.1），
 * 关掉标签页/浏览器就应该消失，不应该无限期躺在本机浏览器里。
 */
const CONNECTION_STORAGE_PREFIX = 'factory-builder:source-adapter-connection:'

function cloneRow(row: unknown): Record<string, unknown> | null {
  if (row == null || typeof row !== 'object' || Array.isArray(row)) return null
  try {
    return structuredClone(row) as Record<string, unknown>
  } catch {
    return JSON.parse(JSON.stringify(row)) as Record<string, unknown>
  }
}

function parseJsonFieldsInPlace(record: Record<string, unknown>, fields: PathExpr[]): void {
  for (const path of fields) {
    const raw = getByPath(record, path)
    if (typeof raw !== 'string') continue
    try {
      const parsed = JSON.parse(raw) as unknown
      const tokens = path.split('.').filter(Boolean)
      if (tokens.length === 1) {
        record[tokens[0]!] = parsed
      }
    } catch {
      const tokens = path.split('.').filter(Boolean)
      if (tokens.length === 1) record[tokens[0]!] = null
    }
  }
}

function pointsFromContainer(container: unknown): SourcePointPreview[] {
  if (container == null || typeof container !== 'object' || Array.isArray(container)) return []
  const out: SourcePointPreview[] = []
  for (const [key, val] of Object.entries(container as Record<string, unknown>)) {
    if (key === 'datafrom') continue
    if (val == null || typeof val !== 'object' || Array.isArray(val)) continue
    const obj = val as Record<string, unknown>
    if (typeof obj.name !== 'string') continue
    out.push({
      key,
      name: obj.name,
      value: obj.value == null ? '' : String(obj.value),
      time: typeof obj.time === 'string' ? obj.time : undefined,
    })
  }
  return out
}

/** 仅读 identity 类型字段的非空真值；空/缺省返回 undefined（绝不返回点位指纹）。 */
function readDeviceTypeValue(record: Record<string, unknown>, sourceShape: SourceShape): string | undefined {
  const path = sourceShape.identity.deviceTypeSourcePath
  if (!path) return undefined
  const t = getByPath(record, path)
  if (t == null || String(t).trim() === '') return undefined
  return String(t)
}

/** UI 分组用：有真类型用真类型，否则点位指纹 / `_default`。 */
function deviceTypeKey(record: Record<string, unknown>, sourceShape: SourceShape, points: SourcePointPreview[]): string {
  const real = readDeviceTypeValue(record, sourceShape)
  if (real) return real
  const fp = points.map((p) => p.key).sort().join(',')
  return fp || '_default'
}

/** 从整包原始响应拆出设备预览（含点位中文名）；并附带 convert 后的 snapshot。 */
export function previewDevicesFromRaw(raw: unknown, sourceShape: SourceShape): SourceDevicePreview[] {
  const envelope = getByPath(raw, sourceShape.envelope)
  if (!Array.isArray(envelope)) return []

  const snapshots = convertSourceToSnapshots(raw, sourceShape)
  const byCode = new Map(snapshots.filter((s) => s.assetCode).map((s) => [s.assetCode!, s]))

  const devices: SourceDevicePreview[] = []
  for (const row of envelope) {
    const record = cloneRow(row)
    if (!record) continue
    parseJsonFieldsInPlace(record, sourceShape.jsonStringFields)

    const assetRaw = getByPath(record, sourceShape.identity.assetCodeSourcePath)
    if (assetRaw == null || assetRaw === '') continue
    const assetCode = String(assetRaw)
    const deviceNameRaw = sourceShape.identity.deviceNameSourcePath
      ? getByPath(record, sourceShape.identity.deviceNameSourcePath)
      : undefined
    const pointsContainer = getByPath(record, resolvePointsContainerPath(sourceShape))
    const points = pointsFromContainer(pointsContainer)
    const typeValue = readDeviceTypeValue(record, sourceShape)
    devices.push({
      assetCode,
      deviceName: deviceNameRaw == null ? undefined : String(deviceNameRaw),
      deviceTypeKey: deviceTypeKey(record, sourceShape, points),
      ...(typeValue ? { deviceTypeValue: typeValue } : {}),
      status: record.status == null ? undefined : String(record.status),
      netStatus: record.netStatus == null ? undefined : String(record.netStatus),
      points,
      snapshot: byCode.get(assetCode),
    })
  }
  return devices
}

export function groupDevicesByType(devices: SourceDevicePreview[]): Map<string, SourceDevicePreview[]> {
  const map = new Map<string, SourceDevicePreview[]>()
  for (const d of devices) {
    const list = map.get(d.deviceTypeKey) ?? []
    list.push(d)
    map.set(d.deviceTypeKey, list)
  }
  return map
}

export function buildAssetMatchReport(
  devices: SourceDevicePreview[],
  objects: SceneObject[],
): AssetMatchReport {
  const sourceCodes = new Map(devices.map((d) => [d.assetCode, d]))
  const sceneWithCode = objects.filter((o) => (o.assetCode ?? '').trim())

  const matched: AssetMatchReport['matched'] = []
  const sceneOnly: AssetMatchReport['sceneOnly'] = []
  const usedSource = new Set<string>()

  for (const obj of sceneWithCode) {
    const code = obj.assetCode!.trim()
    const src = sourceCodes.get(code)
    if (src) {
      matched.push({
        assetCode: code,
        objectId: obj.id,
        objectName: obj.name,
        deviceName: src.deviceName,
      })
      usedSource.add(code)
    } else {
      sceneOnly.push({ assetCode: code, objectId: obj.id, objectName: obj.name })
    }
  }

  const sourceOnly: AssetMatchReport['sourceOnly'] = []
  for (const d of devices) {
    if (!usedSource.has(d.assetCode)) {
      sourceOnly.push({ assetCode: d.assetCode, deviceName: d.deviceName })
    }
  }

  return { matched, sceneOnly, sourceOnly }
}

/**
 * 按组内首台设备的点位，生成「源路径 → 中文名」映射草稿（scale 默认 1）。
 * 疑似需要换算的点位由 UI 侧用 isSuspectScaleValue() 单独提示，正式 scale 只在人工确认后（见 confirmScale）写入。
 */
export function suggestPointFieldMappings(
  sample: SourceDevicePreview,
  options?: { matchBy?: 'machineryCode' | 'deviceType' },
): FieldMapping[] {
  const matchBy = options?.matchBy ?? 'machineryCode'
  const deviceTypeMatch: FieldMapping['deviceTypeMatch'] =
    matchBy === 'deviceType'
      ? { path: 'machineryTypeId', equals: sample.deviceTypeKey }
      : { path: 'machineryCode', equals: sample.assetCode }

  return sample.points.map((p) => ({
    deviceTypeMatch,
    sourcePath: `attr1.${p.key}.value`,
    sourceLabelPath: `attr1.${p.key}.name`,
    targetField: p.name,
    scale: 1,
  }))
}

/** 可疑换算：绝对值很大的数值点位（如温度原始值 2848）。 */
export function isSuspectScaleValue(value: string): boolean {
  const n = Number(value)
  return Number.isFinite(n) && Math.abs(n) >= 500
}

export function loadConnectionDraft(templateId: string): DesignConnectionDraft | null {
  try {
    const raw = sessionStorage.getItem(CONNECTION_STORAGE_PREFIX + templateId)
    if (!raw) return null
    return JSON.parse(raw) as DesignConnectionDraft
  } catch {
    return null
  }
}

export function saveConnectionDraft(draft: DesignConnectionDraft): void {
  sessionStorage.setItem(CONNECTION_STORAGE_PREFIX + draft.templateId, JSON.stringify(draft))
}

export function clearConnectionDraft(templateId: string): void {
  sessionStorage.removeItem(CONNECTION_STORAGE_PREFIX + templateId)
}

/** 生成仅供网关主机的连接实例 JSON（可含密码）；永不写入 SceneData。 */
export function toConnectionInstanceJson(draft: DesignConnectionDraft): Record<string, unknown> {
  return {
    templateId: draft.templateId,
    baseUrl: draft.baseUrl.replace(/\/$/, ''),
    queryParamValues: {
      organizationId: draft.organizationId,
      pageSize: 500,
    },
    credentials: {
      username: draft.username,
      password: draft.password,
    },
    pollIntervalMs: draft.pollIntervalMs,
  }
}

/**
 * 「下载网关连接配置」实际下载的文件：{ template, connectionInstance }，
 * 与 P2 `runtime-gateway.config.json` 的 loadGatewayConfig() 期望的形状一致——
 * 拿到这一份文件直接放到网关主机即可用，不需要再手动从 .f3p 里挖 template。
 * @see src/runtime-gateway/loadGatewayConfig.ts
 */
export function toRuntimeGatewayConfigJson(
  sourceShape: SourceShape,
  draft: DesignConnectionDraft,
): { template: SourceShape; connectionInstance: Record<string, unknown> } {
  return {
    template: sourceShape,
    connectionInstance: toConnectionInstanceJson(draft),
  }
}

export function buildInfoCardFieldsFromMappings(
  mappings: FieldMapping[],
  device: SourceDevicePreview,
): Array<{ key: string; value: string }> {
  const fields: Array<{ key: string; value: string }> = []
  const seen = new Set<string>()
  for (const m of mappings) {
    if (seen.has(m.targetField)) continue
    const match = m.deviceTypeMatch
    if (match && match !== 'default') {
      if (match.path === 'machineryCode' && match.equals !== device.assetCode) continue
      if (match.path === 'machineryTypeId' && match.equals !== device.deviceTypeKey) continue
    }
    seen.add(m.targetField)
    const point = device.points.find((p) => m.sourcePath.includes(`.${p.key}.`))
    fields.push({ key: m.targetField, value: point?.value ?? '' })
  }
  return fields
}

/** 编辑器设计态默认打本机网关预览口（与 P2 默认端口一致）。 */
export const DEFAULT_LOCAL_GATEWAY_PREVIEW_URL = 'http://127.0.0.1:8787/api/preview'

/** 网关 /api/preview 返回 `{ raw }`；也接受直接就是信封的响应。 */
export function unwrapPreviewResponse(body: unknown): unknown {
  if (body && typeof body === 'object' && !Array.isArray(body) && 'raw' in body) {
    return (body as { raw: unknown }).raw
  }
  return body
}

/**
 * 人工确认换算系数后只改当前样例设备的精确匹配条目，绝不改 default 映射。
 */
export function confirmScaleOnMappings(
  mappings: FieldMapping[],
  sample: SourceDevicePreview,
  pointKey: string,
  scale: number,
): FieldMapping[] {
  const sourcePath = `attr1.${pointKey}.value`
  let found = false
  const next = mappings.map((m) => {
    if (m.sourcePath !== sourcePath) return m
    if (typeof m.deviceTypeMatch !== 'object') return m
    if (m.deviceTypeMatch.path !== 'machineryCode' || m.deviceTypeMatch.equals !== sample.assetCode) return m
    found = true
    return { ...m, scale }
  })
  if (!found) {
    const point = sample.points.find((p) => p.key === pointKey)
    next.push({
      deviceTypeMatch: { path: 'machineryCode', equals: sample.assetCode },
      sourcePath,
      sourceLabelPath: `attr1.${pointKey}.name`,
      targetField: point?.name ?? pointKey,
      scale,
    })
  }
  return next
}

/** 场景里已声明的信息卡字段名（附录 A.5）。 */
export function collectSceneInfoCardKeys(objects: SceneObject[]): Set<string> {
  const keys = new Set<string>()
  for (const obj of objects) {
    for (const field of obj.infoCard?.fields ?? []) {
      if (field.key) keys.add(field.key)
    }
  }
  return keys
}

/**
 * 有信息卡字段时，targetField 对不上则标红；场景还没有任何信息卡时不刷红（允许先映射后补字段）。
 */
export function isTargetFieldUnmatched(targetField: string, knownKeys: Set<string>): boolean {
  if (knownKeys.size === 0) return false
  return !knownKeys.has(targetField)
}

export function unmatchedTargetFields(mappings: FieldMapping[], knownKeys: Set<string>): string[] {
  const seen = new Set<string>()
  const out: string[] = []
  for (const m of mappings) {
    if (seen.has(m.targetField)) continue
    seen.add(m.targetField)
    if (isTargetFieldUnmatched(m.targetField, knownKeys)) out.push(m.targetField)
  }
  return out
}

/** 空 conditions 视为总是命中的兜底规则（附录 A.5）。 */
export function hasStatusRulesFallback(rules: SourceShape['statusRules'] | undefined): boolean {
  if (!rules?.length) return false
  return rules.some((r) => r.conditions.length === 0)
}

// ---------------------------------------------------------------------------
// P5-A：自定义模板打磨——错误人话 + 从网关配置反导入
// 结构校验复用 P5-B 已有的 sourceAdapterValidate.ts（validateTemplateConfig / unwrapImportedTemplate），
// 不在这里另开一套——那份校验更严（PathExpr 语法、禁止模板里出现字面密钥、statusRules 兜底位置、
// status 词表告警），Skill 产出和面板导入必须走同一套规则，否则两边判定标准会打架。
// ---------------------------------------------------------------------------

function isNonEmptyString(v: unknown): v is string {
  return typeof v === 'string' && v.length > 0
}

/**
 * 从「下载网关配置」的产物（或手写的同形状 JSON）反解出可回填到设计态草稿的字段。
 * 密码字段一起带回——它本来就来自本机浏览器会话草稿或用户自己下载的文件，信任级别没变，
 * 回填后仍然只进 sessionStorage，不进场景。
 */
export function draftFromConnectionInstance(parsed: unknown): Partial<DesignConnectionDraft> | null {
  if (!parsed || typeof parsed !== 'object' || Array.isArray(parsed)) return null
  const container = parsed as Record<string, unknown>
  const connectionInstance = container.connectionInstance
  if (!connectionInstance || typeof connectionInstance !== 'object' || Array.isArray(connectionInstance)) return null
  const c = connectionInstance as Record<string, unknown>

  const out: Partial<DesignConnectionDraft> = {}
  if (isNonEmptyString(c.templateId)) out.templateId = c.templateId
  if (isNonEmptyString(c.baseUrl)) out.baseUrl = c.baseUrl
  if (c.queryParamValues && typeof c.queryParamValues === 'object') {
    const orgId = (c.queryParamValues as Record<string, unknown>).organizationId
    if (orgId != null) out.organizationId = String(orgId)
  }
  if (c.credentials && typeof c.credentials === 'object') {
    const cred = c.credentials as Record<string, unknown>
    if (typeof cred.username === 'string') out.username = cred.username
    if (typeof cred.password === 'string') out.password = cred.password
  }
  if (typeof c.pollIntervalMs === 'number') out.pollIntervalMs = c.pollIntervalMs
  return Object.keys(out).length ? out : null
}

export interface PullOnceResult {
  devices: SourceDevicePreview[]
  hint?: string
  error?: string
}

/**
 * 「拉一次」的完整决策逻辑，抽成纯函数便于单测（原先直接写在 Vue 组件里，没有测试覆盖）。
 * 失败即真错误：**禁止**回退内置样例设备冒充成功（docs/05-连接数据.md §1.1：试连失败停在本页，红字说明原因）。
 * 旧版「chenhe-mes-v1 网关连不上时回退内置样例夹具」的产品路径已删除（线 A，docs/05-连接数据.md §6）。
 */
export async function pullOncePreview(
  sourceShape: SourceShape,
  previewUrl: string,
  fetchImpl: typeof fetch,
): Promise<PullOnceResult> {
  try {
    const resp = await fetchImpl(previewUrl)
    if (!resp.ok) throw new Error(`网关预览失败（HTTP ${resp.status}）`)
    const body: unknown = await resp.json()
    return { devices: previewDevicesFromRaw(unwrapPreviewResponse(body), sourceShape) }
  } catch (err) {
    const message = explainRuntimeConnectionError(err, { endpoint: previewUrl })
    return { devices: [], error: message }
  }
}
